import { Filter } from '../../../public-filters/domain/valueObjects/Filter';
import { MarketingStatus } from '../../../public-filters/domain/FilterTypes';

/**
 * Maps MarketingStatus (domain concept) to backend filter fields.
 *
 * This is the translation layer between user intent and database structure:
 * - FOR_SALE/FOR_RENT → Active statuses + sale_or_rent field
 * - SOLD/RENTED/etc → Specific status values
 */
function mapMarketingStatusToBackend(marketingStatus: MarketingStatus) {
  switch (marketingStatus) {
    case 'FOR_SALE':
      return {
        saleOrRent: 'for_sale',
        statusFlags: ['active'],
      };
    case 'FOR_RENT':
      return {
        saleOrRent: 'for_rent',
        statusFlags: ['active'],
      };
    case 'SOLD':
      return {
        saleOrRent: 'sold', // Backend handles this specially
        statusFlags: ['sold'],
      };
    case 'RENTED':
      return {
        saleOrRent: 'for_rent',
        statusFlags: [], // Will be mapped to status='Rented' in backend
      };
    case 'WITHDRAWN':
    case 'EXPIRED':
    case 'CANCELLED':
      return {
        saleOrRent: null,
        statusFlags: [], // Will be mapped to specific status in backend
      };
    default:
      // Default to FOR_SALE
      return {
        saleOrRent: 'for_sale',
        statusFlags: ['active'],
      };
  }
}

export function toApiFilter(filter: Filter): any {
  const { saleOrRent, statusFlags } = mapMarketingStatusToBackend(
    filter.marketingStatus
  );

  return {
    saleOrRent,
    statusFlags: filter.statusFlags || statusFlags,
    zipcode: filter.zipcode || undefined,
    city: filter.city || undefined,
    price: filter.price?.toObject() || { min: null, max: null },
    beds: filter.beds?.toObject() || { min: null, max: null },
    baths: filter.baths?.toObject() || { min: null, max: null },
    homeTypes: Array.from(filter.homeTypes || []),
    timeOnMarket: filter.timeOnMarket?.toObject() || { min: null, max: null },
    sqft: filter.sqft?.toObject() || { min: null, max: null },
    lotSize: filter.lotSize?.toObject() || { min: null, max: null },
    stories: filter.stories?.toObject() || { min: null, max: null },
    yearBuilt: filter.yearBuilt?.toObject() || { min: null, max: null },
    pricePerSqft: filter.pricePerSqft?.toObject() || { min: null, max: null },
    propertyTaxes: filter.propertyTaxes?.toObject() || { min: null, max: null },
    sortBy: filter.sortBy || 'best_deal',
    sortDirection: filter.sortDirection || 'desc',
  };
}
